import React, { useState, Fragment, useEffect } from 'react';
import { Tooltip } from 'reactstrap';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import {
  Row,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  CustomInput,
  InputGroupText,
} from 'reactstrap';
import { Colxx } from '../../../../../components/common/CustomBootstrap';
import { AxiosAPI, getGroupID } from '../../../../../helpers/AxiosAPI';
import {
  postAPI_Create_QuickReply,
  gAPI_QuestionSets,
  gAPI_QuickReply_ById,
  putAPI_Update_QuickReply,
  gAPI_QuickReplyDelete_ById,
  gAPI_Twilio_Templates,
} from '../../../../../constants/APIs';
import Validation from '../../../../../components/Validation';

const CHANNEL_OPTIONS = ["chat", "email", "whatsapp", "sms"];

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ar", label: "Arabic" },
  { code: "fr", label: "French" },
  { code: "es", label: "Spanish" },
  { code: "pt_BR", label: "Portuguese (Brazil)" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "id", label: "Indonesian" },
  { code: "vi", label: "Vietnamese" },
];

const TEMPLATE_TYPES = [
  { value: "text", label: "Text" },
  { value: "media", label: "Media (Coming Soon)" },
  { value: "cta", label: "Call to Action (Coming Soon)" },
  { value: "card", label: "Card (Coming Soon)" },
  { value: "quick_reply", label: "Quick Reply (Coming Soon)" },
];

const FIELDS = [
  { key: "guest_first_name", label: "First Name" },
  { key: "guest_last_name", label: "Last Name" },
  { key: "check_in_date", label: "Check-in Date" },
  { key: "check_out_date", label: "Check-out Date" },
  { key: "guest_email", label: "Email Address" },
  { key: "guest_phone", label: "Phone Number" },
  { key: "room_number", label: "Room Number" },
  { key: "room_type", label: "Room Type Name" },
];

export function AddNewReply({
  id,
  visible,
  toggleModal,
  formLabel,
  propertyId,
  domains,
  LoadData,
  editType,
  defaultChannel,
  isChatbot,
  isEmail,
  isSocial,
  isGuestMessaging
}) {

  const [assistantList, setAssistantList] = useState([]);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState([]);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [placeholderMap, setPlaceholderMap] = useState({});
  const [placeholderErrors, setPlaceholderErrors] = useState({});
  const [messageType, setMessageType] = useState("SMS");
  const [templateContentType, setTemplateContentType] = useState("Text");
  // Main form state
  const getInitialState = () => ({
    groupId: getGroupID(),
    name: [
      {
        statement: '',
        languageCode: 'en',
      },
    ],
    message: [
      {
        statement: '',
        languageCode: 'en',
      },
    ],
    dynamicTemplate: '',
    templateType: 'Static',
    templateWidget: 'ComingSoon',
    templateChannel: defaultChannel ? [defaultChannel] : [],
    templateId: '',
    language: '',
    creator: 'dashboard',
  });

  const [quickReplyData, setQuickReplyData] = useState(getInitialState());
  const isEditMode = Boolean(id);
  const isCreateMode = !id;
  const isTemplateEdit = isEditMode && editType === "template";
  const isReplyEdit = isEditMode && editType === "reply";

  // Normalize selected channels
  const channels = Array.isArray(quickReplyData.templateChannel)
    ? quickReplyData.templateChannel
    : typeof quickReplyData.templateChannel === "string"
      ? [quickReplyData.templateChannel]
      : [];
  const channel = (channels[0] || "").toLowerCase();
  const noChannelSelected = channels.length === 0;

  // Channel state flags
  const isChat = channel === "chat";
  const isMail = channel === "email";
  const isWhatsApp = channel === "whatsapp";
  const isSMS = channel === "sms";

  // UI condition flags
  const showDelete = isChat || isMail;
  const isTemplateFlow = isWhatsApp || isSMS;
  const isSMSOnly = isSMS && channels.length === 1;

  //  Load data on open / edit
  useEffect(() => {
    if (!visible) return;
    loadResponse();

    if (isEditMode) {
      fetchEditData();
    } else {
      setQuickReplyData(getInitialState());

      if (defaultChannel === "sms") {
        setMessageType("SMS");
        setTemplateContentType("None");
      } else if (defaultChannel === "whatsapp") {
        setMessageType("None");
        setTemplateContentType("Text");
      } else {
        setMessageType("None");
        setTemplateContentType("None");
      }
    }
  }, [id, visible, editType]);

  // Load existing record for editing
  const fetchEditData = async () => {
    try {
      if (isTemplateEdit) {
        const res = await AxiosAPI({
          method: "get",
          url: `${gAPI_Twilio_Templates}/${id}`,
          params: { group_id: getGroupID() },
        });

        const data = res.data;
        const map = {};
        (data.placeholders || []).forEach(p => {
          map[p.name] = p.value;
        });
        setPlaceholderMap(map);
        setPlaceholderErrors({});
        const selectedChannel = Array.isArray(data.channel)
          ? (data.channel[0] || "").toLowerCase()
          : (data.channel || "")
            .toString()
            .toLowerCase()
            .split(",")[0];

        if (selectedChannel === "sms") {
          setMessageType(data.messageType || "SMS");
          setTemplateContentType("None");
        }

        if (selectedChannel === "whatsapp") {
          setTemplateContentType(data.templateContentType || "Text");
          setMessageType("None");
        }
        setQuickReplyData(prev => ({
          ...prev,
          name: [{ statement: data.name || "", languageCode: "en" }],
          message: [{ statement: data.templateBody || "", languageCode: "en" }],
          templateId: data.templateId || "",
          templateType: data.templateType || "text",
          language: data.language || "",
          templateChannel: (data.channel || "")
            .toString()
            .split(",")
            .map(c => c.toLowerCase().trim())
            .filter(Boolean),
        }));
      }

      if (isReplyEdit) {
        const res = await AxiosAPI({
          method: "get",
          url: `${gAPI_QuickReply_ById}/${id}`,
        });

        const data = res.data;
        setQuickReplyData(prev => ({
          ...prev,
          ...data,
          templateChannel: (data.templateChannel || "")
            .split(",")
            .map(c => c.toLowerCase().trim())
            .filter(Boolean),
        }));
      }
    } catch (err) {
      console.log(err);
    }
  };

  // Fetch assistant/question sets
  const loadResponse = async () => {
    try {
      const res = await AxiosAPI({
        method: 'get',
        url: `${gAPI_QuestionSets}/?propertyId=${propertyId}&groupId=${getGroupID()}&lang=en`,
      });

      const list = res.data.sets
        ?.filter(l => l.type === 'DynamicConversationFlow' && l.openAiAssistantId !== '')
        .map(l => ({
          id: l.openAiAssistantId,
          name: l.name,
        }));

      setAssistantList(list || []);
    } catch (err) {
      console.log(err);
    }
  };

  // Update text fields
  const handleChangeArr = (e) => {
    const { name, value } = e.target;
    setQuickReplyData({
      ...quickReplyData,
      [name]: [{ statement: value, languageCode: 'en' }],
    });
  };

  // Update dynamic template
  const handleDynamicTemplate = (e) => {
    setQuickReplyData({
      ...quickReplyData,
      dynamicTemplate: e.target.value,
    });
  };

  // Update selected channel
  const handleChannelChange = (value) => {
    setQuickReplyData((prev) => ({
      ...prev,
      templateChannel: [value.toLowerCase()],
      templateId: "",
      templateType: value === "email" ? "Static" : "",
      language: "",
    }));

    switch (value.toLowerCase()) {
      case "sms":
        setMessageType("SMS");
        setTemplateContentType("None");
        break;

      case "whatsapp":
        setMessageType("None");
        setTemplateContentType("Text");
        break;

      default:
        setMessageType("None");
        setTemplateContentType("None");
        break;
    }
  };

  const handleCreate = async () => {
    if (isWhatsApp || isSMS) {
      await createTemplate();
    } else if (isChat || isMail) {
      await createQuickReply();
    }
  };

  const handleUpdate = async () => {
    if (isTemplateEdit) {
      await updateTemplate();
    } else if (isReplyEdit) {
      await updateQuickReply();
    }
  };

  // Submit form 
  const handleSubmit = async () => {
    if ((isWhatsApp || isSMS) && !validatePlaceholders()) {
      return;
    }
    if (channels.length === 0) {
      alert("Please select a channel");
      return;
    }
    try {
      if (isCreateMode) {
        await handleCreate();
      } else {
        await handleUpdate();
      }

      LoadData();
      toggleModal();
    } catch (err) {
      console.log(err);
    }
  };

  // delete quick reply
  const handleDelete = () => {
    AxiosAPI({
      method: 'put',
      url: `${gAPI_QuickReplyDelete_ById}/${id}`,
      data: {
        deleter: 'dashboard',
      },
    })
      .then((res) => {
        if (
          res.data.HttpStatusCode !== undefined &&
          res.data.HttpStatusCode === 400 &&
          res.data.Messages !== undefined &&
          res.data.Messages !== null &&
          res.data.Messages.length > 0
        ) {
          setMsg(res.data.Messages);
          setOpen(true);
        } else {
          LoadData();
          toggleModal();
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // Show delete confirmation modal
  const toggle = () => {
    setDeleteVisible(!deleteVisible);
  };

  const generateDeleteButton = () => {
    if (!showDelete) return null;
    return (
      <Row>
        <Colxx xs="12" sm="12" md="12" lg="8" className="mb-4">
          <Row>
            <Colxx xs="12" sm="3" className="mb-1">
              <Modal isOpen={deleteVisible} toggle={toggle}>
                <ModalHeader toggle={toggle}>
                  <span role="img" aria-label="warning">
                    ⚠️
                  </span>
                  This template will be deleted permanently!
                </ModalHeader>
                <ModalBody>
                  <Button color="danger" outline onClick={handleDelete} size="xs">Delete</Button>
                  <Button className="ml-2" color="dark" outline size="xs" onClick={toggle}>Cancel</Button>
                </ModalBody>
              </Modal>
            </Colxx>
          </Row>
        </Colxx>
      </Row>
    );
  };

  const createTemplate = () => {
    return AxiosAPI({
      method: "post",
      url: gAPI_Twilio_Templates,
      params: { group_id: getGroupID() },
      data: buildTemplatePayload(),
    });
  };

  const updateTemplate = () => {
    return AxiosAPI({
      method: "put",
      url: `${gAPI_Twilio_Templates}/${id}`,
      params: { group_id: getGroupID() },
      data: buildTemplatePayload(),
    });
  };

  const createQuickReply = () => {
    return AxiosAPI({
      method: "post",
      url: postAPI_Create_QuickReply,
      data: buildQuickReplyPayload(),
    });
  };

  const updateQuickReply = () => {
    return AxiosAPI({
      method: "put",
      url: `${putAPI_Update_QuickReply}/${id}`,
      data: buildQuickReplyPayload(),
    });
  };

  const buildTemplatePayload = () => ({
    groupId: quickReplyData.groupId,
    propertyId,
    name: quickReplyData.name?.[0]?.statement || "",
    templateBody: quickReplyData.message?.[0]?.statement || "",
    templateId: quickReplyData.templateId,
    templateType: quickReplyData.templateType || "text",
    language: quickReplyData.language || "en",
    channel: quickReplyData.templateChannel,
    placeholders: Object.entries(placeholderMap).map(
      ([name, value]) => ({ name, value })
    ),
    messageType,
    templateContentType,
    createdBy: "dashboard",
  });

  const buildQuickReplyPayload = () => ({
    groupId: quickReplyData.groupId,
    name: quickReplyData.name,
    message: quickReplyData.message,
    dynamicTemplate: quickReplyData.dynamicTemplate,
    templateType: quickReplyData.templateType || "Static",
    templateWidget: quickReplyData.templateWidget || "ComingSoon",
    templateChannel: quickReplyData.templateChannel[0] || "".toLowerCase(),
    creator: "dashboard",
  });

  const extractPlaceholders = (text) =>
    [...new Set(
      (text.match(/{{\s*([\w-]+)\s*}}/g) || [])
        .map(p => p.replace(/[{}]/g, ""))
    )];

  const validatePlaceholders = () => {
    const placeholdersInMessage = extractPlaceholders(
      quickReplyData.message?.[0]?.statement || ""
    );

    const errors = {};
    placeholdersInMessage.forEach(ph => {
      if (!placeholderMap[ph]) {
        errors[ph] = "Required";
      }
    });

    setPlaceholderErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return (
    <Fragment>
      <style>
        {`select option:disabled {
        background-color: #d0d0d0;
        color: #555;
        cursor: not-allowed;
        }`}
      </style>
      <Modal
        isOpen={visible}
        toggle={toggleModal}
        wrapClassName="modal-right"
        backdrop="static"
      >
        <ModalHeader toggle={toggleModal}>{formLabel}</ModalHeader>

        <ModalBody>
          {/* NAME */}
          <Label>Name</Label>
          <Input
            className="input_sty"
            name="name"
            placeholder="Enter Template Name"
            value={quickReplyData.name?.[0]?.statement || ""}
            onChange={handleChangeArr}
          />

          {/* CHANNELS selection*/}
          <Label className="mt-3"> Channel</Label>
          {CHANNEL_OPTIONS.map((ch) => {
            const isChannelEnabled =
              (ch === 'chat' && isChatbot) ||
              (ch === 'email' && isEmail) ||
              ((ch === 'whatsapp' || ch === 'sms') && (isSocial || isGuestMessaging));

            if (!isChannelEnabled) return null;

            return (
              <CustomInput
                type="radio"
                name="channel"
                key={ch}
                label={ch.charAt(0).toUpperCase() + ch.slice(1)}
                checked={channels.includes(ch)}
                onChange={() => handleChannelChange(ch)}
                disabled={isEditMode && !channels.includes(ch)}
              />
            );
          })}

          {
            !isChatbot && !isEmail && !isSocial && !isGuestMessaging && (
              <div style={{
                padding: 10,
                borderRadius: 6,
                color: '#555',
                fontSize: 13
              }}> No channels enabled. Enable integrations in property settings.
              </div>
            )
          }

          {/* EMAIL */}
          {isMail && (
            <>
              <Label className="mt-3">Type</Label>
              <CustomInput
                type="radio"
                id="exCustomStatic"
                name="customStatic"
                label="Static"
                checked={quickReplyData?.templateType === 'Static'}
                onChange={(event) =>
                  setQuickReplyData({
                    ...quickReplyData,
                    templateType: 'Static',
                  })
                }
              />
              <CustomInput
                type="radio"
                id="exCustomDynamic"
                name="customDynamic"
                label="Dynamic"
                checked={quickReplyData?.templateType === 'Dynamic'}
                onChange={(event) =>
                  setQuickReplyData({
                    ...quickReplyData,
                    templateType: 'Dynamic',
                  })
                }
              />

              {/* ✅ DYNAMIC BLOCK FIX */}
              {quickReplyData?.templateType === 'Dynamic' && isMail && (
                <>
                  <Label className="mt-3">Dynamic Template</Label>
                  {assistantList.length > 0 ? (
                    <Input
                      className="border p-2 mb-3 input_sty"
                      type="select"
                      name="dynamicTemplate"
                      placeholder="Dynamic Conversation Flow"
                      value={quickReplyData?.dynamicTemplate || ""}
                      onChange={handleDynamicTemplate}
                    >
                      <option value="">
                        Select a Dynamic Conversation Flow
                      </option>
                      {assistantList.map((assist, index) => (
                        <option key={index} value={assist.id}>
                          {`${assist.name} (${assist.id})`}
                        </option>
                      ))}
                    </Input>
                  ) : (
                    <Input className='input_sty'
                      type="text"
                      onChange={handleDynamicTemplate}
                      value={quickReplyData.dynamicTemplate || ""}
                      name="dynamicTemplate"
                      placeholder="Dynamic Conversation Flow"
                    />
                  )}

                  <Label>Widget</Label>
                  <CustomInput
                    type="radio"
                    id="exCustomRatesAndAvailability"
                    name="customRatesAndAvailability"
                    label="Rates & Availability"
                    checked={
                      quickReplyData?.templateWidget === 'RatesAndAvailability'
                    }
                    onChange={(event) =>
                      setQuickReplyData({
                        ...quickReplyData,
                        templateWidget: 'RatesAndAvailability',
                      })
                    }
                  />
                  <CustomInput
                    type="radio"
                    id="exCustomComingSoon"
                    name="customComingSoon"
                    label="Coming Soon"
                    checked={quickReplyData?.templateWidget === 'ComingSoon'}
                    onChange={(event) =>
                      setQuickReplyData({
                        ...quickReplyData,
                        templateWidget: 'ComingSoon',
                      })
                    }
                  />
                </>
              )}
            </>
          )}

          {/* WHATSAPP + SMS */}
          {(isWhatsApp || isSMS) && (
            <>
              <Label className="mt-3">Template Id</Label>
              <Input
                className="input_sty"
                placeholder={
                  isSMS && messageType === "SMS"
                    ? "Will be generated automatically"
                    : "Enter Template Id"
                }
                value={quickReplyData.templateId || ""}
                onChange={(e) =>
                  setQuickReplyData({
                    ...quickReplyData,
                    templateId: e.target.value,
                  })
                }
                disabled={isSMS && messageType === "SMS"}
                style={{
                  backgroundColor:
                    isSMS && messageType === "SMS" ? "#f5f5f5" : "",
                  cursor:
                    isSMS && messageType === "SMS" ? "not-allowed" : "text",
                  opacity:
                    isSMS && messageType === "SMS" ? 0.7 : 1,
                }}
              />
              {isSMS && (
                <>
                  <Label className="mt-3">Message Type</Label>

                  <CustomInput
                    type="radio"
                    id="message-type-sms"
                    name="messageType"
                    label="SMS"
                    value="SMS"
                    checked={messageType === "SMS"}
                    onChange={(e) => setMessageType(e.target.value)}
                  />
                </>
              )}

              {isWhatsApp && (
                <>
                  <Label className="mt-3">Template Content Type</Label>

                  <CustomInput
                    type="radio"
                    id="template-content-text"
                    name="templateContentType"
                    label="Text"
                    value="Text"
                    checked={templateContentType === "Text"}
                    onChange={(e) => setTemplateContentType(e.target.value)}
                  />
                  <CustomInput
                    type="radio"
                    id="template-content-card"
                    name="templateContentType"
                    label="Card"
                    value="Card"
                    checked={templateContentType === "Card"}
                    onChange={(e) => setTemplateContentType(e.target.value)}
                  />
                </>
              )}
              {/* <Label className="mt-3">Template Type</Label>
            <Input
              className="input_sty"
              type="select"
              value={quickReplyData.templateType || ""}
              onChange={(e) =>
                setQuickReplyData((prev) => ({
                  ...prev,
                  templateType: e.target.value,
                }))
              }
            >
              <option value="">Select Template Type</option>

              {TEMPLATE_TYPES
                .filter((t) =>
                  (noChannelSelected || isSMSOnly)
                    ? t.value === "text"
                    : true
                )
                .map((t) => {
                  const disabled = isWhatsApp && t.value !== "text";

                  return (
                    <option
                      key={t.value}
                      value={t.value}
                      disabled={disabled}
                    >
                      {t.label}
                    </option>
                  );
                })}
            </Input> */}

              <Label className="mt-3">Language</Label>
              <Input
                className="input_sty"
                type="select"
                value={quickReplyData.language || ""}
                onChange={(e) =>
                  setQuickReplyData({
                    ...quickReplyData,
                    language: e.target.value,
                  })
                }
              >
                <option value="">Select Language</option>
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </Input>
            </>
          )}

          {/* MESSAGE */}
          <Label className="mt-3"> Message</Label>
          <Input
            className="input_sty mb-2"
            type="textarea"
            rows={4}
            name="message"
            placeholder="Enter Template Message"
            value={quickReplyData.message?.[0]?.statement || ""}
            onChange={handleChangeArr}
          />

          {(isWhatsApp || isSMS) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr" }}>
              {extractPlaceholders(
                quickReplyData.message?.[0]?.statement || ""
              ).map((ph) => {
                const hasError = placeholderErrors[ph];

                return (
                  <div key={ph} style={{ marginBottom: 5 }}>
                    <InputGroup
                      style={{
                        border: hasError ? "1px solid red" : "1px solid #ddd",
                        borderRadius: 6,
                      }}
                    >
                      {/* LEFT */}
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText style={{
                          background: "#f8f9fa",
                          fontWeight: 600,
                          minWidth: 120,
                          justifyContent: "center",
                        }}>
                          {`{{${ph}}}`}
                        </InputGroupText>
                      </InputGroupAddon>

                      {/* RIGHT */}
                      <Input
                        className="input_sty"
                        type="select"
                        value={placeholderMap[ph] || ""}
                        onChange={(e) => {
                          setPlaceholderMap({
                            ...placeholderMap,
                            [ph]: e.target.value,
                          });

                          setPlaceholderErrors((prev) => {
                            const copy = { ...prev };
                            delete copy[ph];
                            return copy;
                          });
                        }}
                      >
                        <option value="">Select Field</option>
                        {FIELDS.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </Input>
                    </InputGroup>

                    {hasError && (
                      <div style={{ color: "red", fontSize: 12 }}>
                        Selection required
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="primary" onClick={handleSubmit}>
            {id ? "Update" : "Save"}
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
          {id && showDelete && (
            <Button color="danger" onClick={toggle}>
              Delete
            </Button>
          )}
        </ModalFooter>
      </Modal>
      {generateDeleteButton()}
      <Validation
        open={open}
        messages={msg}
        toggle={() => {
          setOpen(!open);
        }}
      ></Validation>
    </Fragment>
  );
}

const mapStateToProps = ({ settings }) => {
  const { propertyId, domains } = settings;
  return {
    propertyId,
    domains,
  };
};

export default injectIntl(connect(mapStateToProps, {})(AddNewReply));
